/**
 * PRP-MCP-1 web client: MCP connection status, personal access tokens,
 * proposal review and the OAuth consent screen, over HTTP.
 *
 * Its own module rather than more entries in `BackendAPI`: `web/src/lib/api.ts`
 * sits at its grandfathered god-file ceiling, so this follows the
 * `propagationReviewsApi.ts` precedent. The shared `api` instance attaches the
 * Firebase token and unwraps `response.data`; failures arrive as `ApiError`
 * (message = the backend `detail`, `status` = HTTP status).
 */
import { api } from './api';

/** How the signed-in user is entitled to MCP. */
/** 'free' since PRP-MCP-8 (every account connects); 'none' only from older servers. */
export type McpAccess = 'subscription' | 'role' | 'grant' | 'trial' | 'free' | 'none';

/** GET /mcp/status. */
export interface McpStatus {
  /** False when MCP_ALLOWED_USERS is set and this user is not on it. Absent on older servers. */
  allowed?: boolean;
  plan: string | null;
  access: McpAccess;
  trial_days_left: number | null;
  trial_ends_at: string | null;
  /** Export of chat work (PRP-MCP-8): locked for free and lapsed accounts. Absent on older servers. */
  export?: 'locked' | 'unlocked';
  /** Estimate allowance; null means unlimited. */
  allowance: number | null;
  /** 'lifetime' for the free plan, 'month' otherwise. Absent on older servers (= month). */
  allowance_period?: 'month' | 'lifetime';
  /** Estimates used in the allowance period (older servers: this month only). */
  used_this_month: number;
  /** Null means unlimited. */
  remaining: number | null;
  server_url: string;
}

/** One personal access token (never includes the secret). */
export interface McpToken {
  id: string;
  name: string;
  prefix: string;
  created_at: string;
  last_used_at: string | null;
  revoked_at: string | null;
  /** When the token stops working; null means it lasts until revoked. */
  expires_at?: string | null;
}

/** POST /mcp/tokens: the plaintext `token` is returned exactly once. */
export interface McpCreatedToken extends McpToken {
  token: string;
}

/** Proposal lifecycle states. */
export type McpProposalStatus =
  | 'parked'
  | 'pending'
  | 'approved'
  | 'rejected'
  | 'withdrawn'
  | 'failed';

/** One option a parked proposal offers. */
export interface McpProposalOption {
  choice: string;
  description: string;
}

/** One proposed change from a chat client. */
export interface McpProposal {
  proposal_id: string;
  estimate_id: string;
  kind: string;
  summary: string;
  status: McpProposalStatus;
  created_at: string;
  decided_at: string | null;
  question?: string | null;
  options?: McpProposalOption[] | null;
  reason?: string | null;
}

/** POST /mcp/proposals/approve-all. */
export interface McpApproveAllResult {
  estimate_id: string;
  results: McpProposal[];
  approved: number;
  failed: number;
}

/** PRP-MCP-5: where a chat change landed in the estimate. */
export interface McpLineRef {
  line_number: number;
  line_suffix: string;
}

/** PRP-MCP-5: one applied chat change (the change log row). */
export interface McpChange {
  change_id: string;
  batch_id: string;
  created_at: string;
  client_name: string | null;
  kind: string;
  summary: string;
  room: string | null;
  room_code: string | null;
  line_ref: McpLineRef | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  request_summary: string | null;
  undone: boolean;
  undone_at: string | null;
}

/** PRP-MCP-5: one tool call's worth of changes. */
export interface McpBatch {
  batch_id: string;
  created_at: string;
  count: number;
  undone: boolean;
}

/** PRP-MCP-5: a question the chat app must answer before anything changes. */
export interface McpParkedQuestion {
  proposal_id: string;
  question: string;
  options: McpProposalOption[];
}

/** GET /mcp/estimates/{id}/changes. */
export interface McpEstimateChanges {
  estimate_id: string;
  /** Newest first. */
  changes: McpChange[];
  batches: McpBatch[];
  parked: McpParkedQuestion[];
}

/** GET /mcp/estimates/{id}/version. */
export interface McpEstimateVersion {
  version: string;
  parked_count: number;
}

/** GET /mcp/oauth/requests/{id}. */
export interface McpOAuthRequest {
  request_id: string;
  client_name: string;
  redirect_host: string;
  /** Full redirect target; the client name is self-declared, this is not. */
  redirect_uri: string;
  /** Display name when the redirect host is a known chat app, else null. */
  known_app: string | null;
  scopes: string[];
}

/** A chat app holding a live OAuth connection. */
export interface McpConnection {
  client_id: string;
  name: string;
  redirect_host: string;
  connected_at: string | null;
}

/**
 * True only for an https URL, or http to a loopback host. The consent page
 * navigates to the backend's redirect_url on this origin, so a javascript: or
 * data: URL would run here; the backend refuses those too, this is the second lock.
 */
export function isSafeRedirect(url: string): boolean {
  try {
    const u = new URL(url);
    if (u.protocol === 'https:') return true;
    return u.protocol === 'http:' && ['localhost', '127.0.0.1', '[::1]'].includes(u.hostname);
  } catch {
    return false;
  }
}

/** Approve/deny response: where to send the browser next. */
export interface McpOAuthDecision {
  redirect_url: string;
}

/** GET/POST /mcp/consent (PRP-MCP-9 G5): the assistant consent and whether it is accepted. */
export interface McpConsent {
  version: number;
  text: string;
  accepted: boolean;
  /** When the current version was accepted (ISO), or null. */
  accepted_at: string | null;
}

/** PRP-INTAKE-1: one candidate an open item offers. */
export interface McpOpenItemOption {
  choice: number;
  label: string;
  code: string | null;
}

/** PRP-INTAKE-1: one room an open item sits on (each room is its own proposal). */
export interface McpOpenItemRoom {
  proposal_id: string;
  room: string;
}

/** PRP-INTAKE-1: one open item (a parked question, grouped across rooms by request). */
export interface McpOpenItem {
  key: string;
  question: string;
  request: string;
  kind: 'item' | 'calc' | 'dims' | 'other';
  options: McpOpenItemOption[];
  recommended: number | null;
  reason: string | null;
  status: 'needs a choice';
  rooms: McpOpenItemRoom[];
  answerable: boolean;
}

/** PRP-INTAKE-1: a question settled by a default and recorded as an assumption. */
export interface McpOpenItemAssumption {
  request: string;
  choice: string;
  default: string;
  rooms: string[];
}

/** GET /mcp/estimates/{id}/open-items. */
/** PRP-FACTS-1: one fact the chat saved, with where it came from and how sure it is. */
export type ClaimFactConfidence = 'seen' | 'inferred' | 'unknown';
export interface ClaimFactValue {
  value?: unknown;
  confidence?: ClaimFactConfidence;
  source?: string;
  evidence?: string;
}
export interface ClaimFactRoom {
  name: string;
  level?: number | string | null;
  type?: string;
  length_ft?: number | null;
  width_ft?: number | null;
  height_ft?: number | null;
  dimension_source?: string;
  confidence?: ClaimFactConfidence;
  source?: string;
}
export interface ClaimFactFinish extends ClaimFactValue {
  words?: string;
  count?: number;
}
export interface ClaimFactFixture {
  item: string;
  count?: number;
  confidence?: ClaimFactConfidence;
  source?: string;
}
export interface ClaimFacts {
  header?: Record<string, ClaimFactValue>;
  rooms?: ClaimFactRoom[];
  finishes?: Record<string, Record<string, ClaimFactFinish>>;
  fixtures_dr?: Record<string, ClaimFactFixture[]>;
  damage_notes?: { note_id: string; room?: string; text?: string; source?: string }[];
  carrier_estimate?: { present?: boolean; file?: string; confidence?: ClaimFactConfidence; source?: string };
  other_claim_notes?: { area?: string; note_id?: string; text?: string; source?: string }[];
  unknowns?: { what?: string; room?: string; reason?: string }[];
}
/** PRP-DAMAGE-1: one damage mark as the page shows it (labels only: no codes, rule ids or prices). */
export type ClaimDamageStatus = 'lines' | 'open_item' | 'awaiting_confirmation' | 'rejected' | 'no_line';
export interface ClaimDamageMark {
  mark_id: string;
  room: string;
  surface?: string;
  surface_label?: string;
  material?: string | null;
  work?: string | null;
  work_label?: string | null;
  extent_label?: string | null;
  cause?: string | null;
  confidence?: string;
  source?: string;
  source_kind?: 'note' | 'photo';
  source_text?: string | null;
  evidence?: string | null;
  status: ClaimDamageStatus;
  line_count?: number;
  reason?: string | null;
}
/** PRP-DAMAGE-1: how every damage note ended (D4). */
export interface ClaimNotesCoverage {
  total: number;
  marked: number;
  open_item: number;
  no_scope: number;
  unresolved: number;
}
/** GET /mcp/projects/{id}/claim-facts (labels only, never the app's match terms or codes). */
export interface ClaimFactsResponse {
  project_id: string;
  facts: ClaimFacts | null;
  notes?: { note_id: string; room?: string; state: string; reason?: string }[];
  /** PRP-DAMAGE-1: absent on facts without a damage section (schema 1). */
  damage?: ClaimDamageMark[];
  notes_coverage?: ClaimNotesCoverage;
  updated_at?: string | null;
  vocab_labels?: {
    finishes: Record<string, { label: string; values: Record<string, string> }>;
    fixtures: Record<string, string>;
  };
}

/** PRP-DAMAGE-1: one question settled by its recommended choice, changeable on the page. */
export interface InterviewAssumption {
  id: string;
  question: string;
  choice: number;
  label: string;
  reason?: string | null;
  choices: { choice: number; label: string }[];
}
/** GET /mcp/projects/{id}/interview (owner only; 404 when the project has none). */
export interface ProjectInterview {
  groups?: unknown[];
  budget?: { asked: number; per_room: number; per_claim: number };
  assumptions?: { group: string; items: InterviewAssumption[] }[];
}

export interface McpOpenItems {
  estimate_id: string;
  project_name: string | null;
  review_prompt: string;
  items: McpOpenItem[];
  assumptions: McpOpenItemAssumption[];
}

/** One result from answering open items. */
export interface McpOpenItemResult {
  proposal_id: string;
  status: string;
  summary?: string;
  reason?: string;
}

/** POST /mcp/estimates/{id}/open-items/answer and /accept-recommended. */
export interface McpOpenItemsAnswer {
  results: McpOpenItemResult[];
  open_count: number;
}

export const McpAPI = {
  /** Access, allowance and the server URL for the signed-in user. */
  getStatus: (): Promise<McpStatus> => api.get('/mcp/status'),

  /** The assistant consent text, its version, and whether this user accepted it. */
  getConsent: (): Promise<McpConsent> => api.get('/mcp/consent'),

  /** Accepts the given (current) version; the server answers 409 for any other. */
  acceptConsent: (version: number): Promise<McpConsent> => api.post('/mcp/consent', { version }),

  /** The user's tokens, including revoked ones. */
  listTokens: (): Promise<{ tokens: McpToken[] }> => api.get('/mcp/tokens'),

  /** Creates a token; the response carries the plaintext once. Optional expiry in days. */
  createToken: (name: string, expiresInDays?: number): Promise<McpCreatedToken> =>
    api.post('/mcp/tokens', expiresInDays ? { name, expires_in_days: expiresInDays } : { name }),

  /** Revokes a token by id. */
  revokeToken: (id: string): Promise<{ revoked: string }> =>
    api.delete(`/mcp/tokens/${encodeURIComponent(id)}`),

  /** Lists proposals, optionally filtered by estimate and status. */
  listProposals: (
    params: { estimate_id?: string; status?: McpProposalStatus } = {},
  ): Promise<{ proposals: McpProposal[]; count: number }> => {
    const query: Record<string, string> = {};
    if (params.estimate_id) query.estimate_id = params.estimate_id;
    if (params.status) query.status = params.status;
    return api.get('/mcp/proposals', { params: query });
  },

  /** Approves one proposal (402 when over allowance or not entitled). */
  approveProposal: (id: string): Promise<McpProposal> =>
    api.post(`/mcp/proposals/${encodeURIComponent(id)}/approve`),

  /** Rejects one proposal. */
  rejectProposal: (id: string): Promise<McpProposal> =>
    api.post(`/mcp/proposals/${encodeURIComponent(id)}/reject`),

  /** Approves every pending proposal on one estimate. */
  approveAll: (estimateId: string): Promise<McpApproveAllResult> =>
    api.post('/mcp/proposals/approve-all', { estimate_id: estimateId }),

  /** PRP-MCP-5: the estimate's chat change log, batches and open questions. */
  getChanges: (estimateId: string): Promise<McpEstimateChanges> =>
    api.get(`/mcp/estimates/${encodeURIComponent(estimateId)}/changes`),

  /** PRP-MCP-5: the estimate's current version plus the parked count. */
  getVersion: (estimateId: string): Promise<McpEstimateVersion> =>
    api.get(`/mcp/estimates/${encodeURIComponent(estimateId)}/version`),

  /** PRP-FACTS-1: the claim facts the chat saved for a project (read-only). */
  getClaimFacts: (projectId: string): Promise<ClaimFactsResponse> =>
    api.get(`/mcp/projects/${encodeURIComponent(projectId)}/claim-facts`),

  /** PRP-DAMAGE-1: the project interview: asked groups, the budget, and the assumptions. */
  getInterview: (projectId: string): Promise<ProjectInterview> =>
    api.get(`/mcp/projects/${encodeURIComponent(projectId)}/interview`),

  /** PRP-DAMAGE-1: change interview answers (same semantics as the chat's answer_open_items). */
  answerInterview: (projectId: string, answers: { id: string; choice: number }[]): Promise<unknown> =>
    api.post(`/mcp/projects/${encodeURIComponent(projectId)}/interview/answer`, { answers }),


  /** PRP-INTAKE-1: the estimate's open items, grouped by request, with recommendations. */
  getOpenItems: (estimateId: string): Promise<McpOpenItems> =>
    api.get(`/mcp/estimates/${encodeURIComponent(estimateId)}/open-items`),

  /** PRP-INTAKE-1: answer one open item (same server path the chat uses). */
  answerOpenItem: (
    estimateId: string,
    body: { proposal_id: string; choice: number; same_request: boolean },
  ): Promise<McpOpenItemsAnswer> =>
    api.post(`/mcp/estimates/${encodeURIComponent(estimateId)}/open-items/answer`, body),

  /** PRP-INTAKE-1: answer every open item that has a recommendation with it. */
  acceptRecommended: (estimateId: string): Promise<McpOpenItemsAnswer> =>
    api.post(`/mcp/estimates/${encodeURIComponent(estimateId)}/open-items/accept-recommended`),

  /** PRP-MCP-5: undo one chat change (400/409 carry the reason in the message). */
  undoChange: (changeId: string): Promise<unknown> =>
    api.post(`/mcp/changes/${encodeURIComponent(changeId)}/undo`),

  /** PRP-MCP-5: undo every change from one chat tool call. */
  undoBatch: (batchId: string): Promise<unknown> =>
    api.post(`/mcp/batches/${encodeURIComponent(batchId)}/undo`),

  /** Chat apps connected through OAuth. */
  listConnections: (): Promise<{ connections: McpConnection[] }> => api.get('/mcp/connections'),

  /** Disconnects a chat app: revokes every token it holds for this user. */
  revokeConnection: (clientId: string): Promise<{ revoked: string }> =>
    api.delete(`/mcp/connections/${encodeURIComponent(clientId)}`),

  /** Loads an OAuth consent request (404 when expired). */
  getOAuthRequest: (requestId: string): Promise<McpOAuthRequest> =>
    api.get(`/mcp/oauth/requests/${encodeURIComponent(requestId)}`),

  /**
   * Allows an OAuth consent request. Sends cookies: the backend checks the one
   * it set on the browser that opened the sign-in, so a forwarded consent link
   * cannot be approved from another browser.
   */
  approveOAuthRequest: (requestId: string): Promise<McpOAuthDecision> =>
    api.post(`/mcp/oauth/requests/${encodeURIComponent(requestId)}/approve`, undefined, {
      withCredentials: true,
    }),

  /** Denies an OAuth consent request. */
  denyOAuthRequest: (requestId: string): Promise<McpOAuthDecision> =>
    api.post(`/mcp/oauth/requests/${encodeURIComponent(requestId)}/deny`),
};
